import { useState, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    id: '01',
    title: 'AI职场沟通辅助引擎',
    subtitle: '第一层 · 日常渗透',
    description: '轻量级嵌入邮件、企业微信、钉钉，在日常沟通中无痕完成心理评估与微干预。实时分析情绪倾向，即时提醒措辞建议，从源头减少人际摩擦。',
    image: '/images/ai-engine.jpg',
  },
  {
    id: '02',
    title: '【决战13周】销售思维训练桌游',
    subtitle: '第二层 · 精准定位',
    description: '封装顶尖销售攻单系统思维，让损失发生在实战之前。',
    image: '/images/boardgame-workshop.jpg',
  },
  {
    id: '02+',
    title: 'REFRAME【重构】',
    subtitle: '精准定位后 · 深度融合',
    description: 'AI融合式组织建设沙盘，聚焦战略一致、协作效率、提升沟通效果、拆除跨部门墙等主题，将团队拓展、组织诊断、团队建设、组织重塑通过一场深度参与演练融为一体。',
    image: '/images/reframe-sandbox.jpg',
  },
  {
    id: '03',
    title: '主题工作坊和积极心理研学',
    subtitle: '第三层 · 靶向提升',
    description: '非暴力沟通、复原力训练、情绪视窗、心理资本、艺术疗愈等十大主题，靶向提升心理资本。数据驱动课程设计，效果验证闭环。',
    image: '/images/service-workshop.jpg',
  },
  {
    id: '04',
    title: '持续护航服务',
    subtitle: '第四层 · 长久守护',
    description: '7X12小时情绪热线、驻场咨询师、绿色医疗转介通道、年度心理测评。从「项目」到「陪伴」，构建日常有感知、危机有通道的全方位保障体系。',
    image: '/images/ceramic-hands.jpg',
  },
];

interface ShowcaseSectionProps {
  activeService?: number;
  onServiceChange?: (i: number) => void;
}

export default function ShowcaseSection({ activeService, onServiceChange }: ShowcaseSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (activeService !== undefined && activeService !== active) {
      setActive(activeService);
    }
  }, [activeService]);

  const handleSelect = (i: number) => {
    if (i === active) return;
    setActive(i);
    onServiceChange?.(i);
  };

  useEffect(() => {
    const img = imageRef.current;
    const txt = textRef.current;
    if (img) {
      gsap.fromTo(img, { opacity: 0.6, scale: 1.02 }, { opacity: 1, scale: 1, duration: 0.5, ease: 'power2.out' });
    }
    if (txt) {
      gsap.fromTo(txt, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', delay: 0.1 });
    }
  }, [active]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.from(section, {
      opacity: 0,
      y: 40,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: section,
        start: 'top bottom-=10%',
        toggleActions: 'play none none reverse',
      },
    });
  }, []);

  const current = services[active];

  return (
    <section
      id="showcase"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ backgroundColor: '#fcfcfc' }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          backgroundColor: 'rgba(21, 20, 20, 0.08)',
          transformOrigin: 'left center',
          animation: 'line-expand-h 1.5s ease-out forwards',
        }}
      />

      {/* Header */}
      <div className="px-6 lg:px-12 pt-16 pb-8">
        <p className="text-caption text-umber mb-2">服务体系</p>
        <h2
          className="text-h2"
          style={{ color: '#b89850', textShadow: '1px 1px 0 rgba(255,255,255,0.5), 2px 2px 0 rgba(200,170,120,0.15)' }}
        >
          四层递进，人智协同
        </h2>
        <p
          className="mt-3 max-w-lg"
          style={{ fontSize: '0.9rem', lineHeight: 1.6, color: '#6e5e50' }}
        >
          AI引擎发现异常 <span style={{ color: '#E8A838' }}>-</span> AI+游戏化精准诊断 <span style={{ color: '#E8A838' }}>-</span> 主题工作坊精准突破 <span style={{ color: '#E8A838' }}>-</span> 情绪热线持续护航
        </p>
      </div>

      {/* Main content: Left image + Right nav */}
      <div className="px-6 lg:px-12 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left: Image + description */}
          <div className="lg:col-span-7">
            <div
              ref={imageRef}
              className="relative overflow-hidden"
              style={{
                aspectRatio: '4/3',
                borderRadius: '2px',
              }}
            >
              <img
                src={current.image}
                alt={current.title}
                className="w-full h-full object-cover"
                style={{ willChange: 'transform' }}
              />
            </div>

            <div ref={textRef} className="mt-6">
              <div className="flex items-baseline gap-3 mb-3">
                <span
                  className="font-display"
                  style={{
                    fontSize: '2.5rem',
                    fontWeight: 300,
                    lineHeight: 1,
                    color: 'rgba(21, 20, 20, 0.08)',
                  }}
                >
                  {current.id}
                </span>
                <span className="text-caption text-umber">{current.subtitle}</span>
              </div>

              <h3
                className="font-serif mb-3"
                style={{
                  fontSize: 'clamp(1.2rem, 2vw, 1.6rem)',
                  fontWeight: 400,
                  lineHeight: 1.2,
                  color: '#151414',
                }}
              >
                {current.title}
              </h3>

              <p
                style={{
                  fontSize: '0.9rem',
                  lineHeight: 1.7,
                  color: '#6e5e50',
                  maxWidth: '520px',
                }}
              >
                {current.description}
              </p>
            </div>
          </div>

          {/* Right: Service navigation list */}
          <div className="lg:col-span-5">
            <div
              className="lg:sticky"
              style={{ top: '100px' }}
            >
              <p
                className="text-caption mb-4"
                style={{ color: '#a88868', letterSpacing: '0.1em' }}
              >
                五类服务
              </p>

              <div className="space-y-0">
                {services.map((service, i) => (
                  <button
                    key={service.id}
                    onClick={() => handleSelect(i)}
                    className="interactive w-full text-left transition-all duration-300"
                    style={{
                      padding: '16px 20px',
                      borderTop: i === 0 ? '1px solid rgba(200, 170, 130, 0.2)' : '1px solid rgba(200, 170, 130, 0.1)',
                      borderBottom: i === services.length - 1 ? '1px solid rgba(200, 170, 130, 0.2)' : 'none',
                      borderLeft: active === i ? '3px solid #b89850' : '3px solid transparent',
                      backgroundColor: active === i ? 'rgba(184, 152, 80, 0.06)' : 'transparent',
                    }}
                  >
                    <div className="flex items-baseline gap-2 mb-1">
                      <span
                        className="font-display"
                        style={{
                          fontSize: '1.4rem',
                          fontWeight: 300,
                          lineHeight: 1,
                          color: active === i ? '#b89850' : 'rgba(21, 20, 20, 0.15)',
                          transition: 'color 0.3s',
                        }}
                      >
                        {service.id}
                      </span>
                      <span
                        style={{
                          fontSize: '0.7rem',
                          color: active === i ? '#a08050' : 'rgba(110, 94, 80, 0.4)',
                          transition: 'color 0.3s',
                        }}
                      >
                        {service.subtitle}
                      </span>
                    </div>
                    <h4
                      style={{
                        fontSize: '0.88rem',
                        fontWeight: active === i ? 500 : 400,
                        lineHeight: 1.4,
                        color: active === i ? '#5a4a3a' : '#8a7a68',
                        transition: 'color 0.3s, font-weight 0.3s',
                      }}
                    >
                      {service.title}
                    </h4>
                  </button>
                ))}
              </div>

              {/* CTA */}
              <button
                className="interactive mt-6 w-full py-3.5 text-caption transition-all duration-300 hover:opacity-90"
                style={{
                  backgroundColor: '#151414',
                  color: '#fcfcfc',
                  borderRadius: '2px',
                }}
                onClick={() => {
                  const el = document.getElementById('custom');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                获取定制方案
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ backgroundColor: 'rgba(21, 20, 20, 0.08)' }}
      />
    </section>
  );
}
